import React from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Table,
  Tag,
  Alert,
  Radio,
  Empty,
} from "antd";
import { BankOutlined, LinkOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  MATCH_LABELS,
  normalizeEmail,
  type LookupResult,
} from "./botAdminShared";

const { Title, Text, Paragraph } = Typography;

interface BotCompanyStepProps {
  searched: boolean;
  results: LookupResult[];
  matchedUserEmails: string[];
  selectedCompanyId: number | null;
  onSelectCompany: (id: number) => void;
  onBack: () => void;
  onContinue: () => void;
}

export const BotCompanyStep: React.FC<BotCompanyStepProps> = ({
  searched,
  results,
  matchedUserEmails,
  selectedCompanyId,
  onSelectCompany,
  onBack,
  onContinue,
}) => {
  const selectedResult =
    results.find((r) => r.company.id === selectedCompanyId) ?? null;

  return (
    <Card
      title={
        <Space>
          <BankOutlined />
          <span>Step 2 — Pick company</span>
        </Space>
      }
      extra={
        <Button type="link" onClick={onBack}>
          ← Back to resolve
        </Button>
      }
      style={{ marginBottom: 24 }}
    >
      {!searched || results.length === 0 ? (
        <Empty description="Run resolve first" />
      ) : (
        <>
          {matchedUserEmails.length > 0 && (
            <Alert
              style={{ marginBottom: 16 }}
              type="success"
              showIcon
              message="Matched user emails"
              description={matchedUserEmails.join(", ")}
            />
          )}

          <Paragraph type="secondary">
            {results.length === 1
              ? "One company matched — confirm or continue."
              : `${results.length} companies matched. Pick which tenant the bot should use.`}
          </Paragraph>

          <Radio.Group
            value={selectedCompanyId}
            onChange={(e) => onSelectCompany(e.target.value)}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              {results.map(({ company, users }) => (
                <Card
                  key={company.id}
                  size="small"
                  type={
                    selectedCompanyId === company.id ? "inner" : undefined
                  }
                  style={{
                    borderColor:
                      selectedCompanyId === company.id
                        ? "#1890ff"
                        : undefined,
                  }}
                  title={
                    <Radio value={company.id}>
                      <Space wrap>
                        <Text strong>{company.name}</Text>
                        <Text type="secondary">#{company.id}</Text>
                        {company.status && <Tag>{company.status}</Tag>}
                      </Space>
                    </Radio>
                  }
                  extra={
                    <Link to={`/admin/companies?id=${company.id}`}>
                      <Button size="small" icon={<LinkOutlined />}>
                        Open
                      </Button>
                    </Link>
                  }
                >
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <div>
                      <Text type="secondary">Match via: </Text>
                      {company.matchReasons.map((reason) => (
                        <Tag key={reason} color="blue">
                          {MATCH_LABELS[reason]}
                        </Tag>
                      ))}
                    </div>
                    <Text>
                      Invoice email: {company.invoice_email || "—"}
                    </Text>
                    <Text>Phone: {company.phone_number || "—"}</Text>
                    <Text type="secondary">
                      {[company.city, company.country]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </Text>
                    <Text type="secondary">
                      {users.length} linked user
                      {users.length === 1 ? "" : "s"}
                    </Text>
                  </Space>
                </Card>
              ))}
            </Space>
          </Radio.Group>

          {selectedResult && (
            <>
              <Title level={5} style={{ marginTop: 24 }}>
                Users in {selectedResult.company.name} (
                {selectedResult.users.length})
              </Title>
              <Table
                size="small"
                rowKey="id"
                pagination={
                  selectedResult.users.length > 10 ? { pageSize: 10 } : false
                }
                dataSource={selectedResult.users}
                columns={[
                  {
                    title: "Email",
                    dataIndex: "email",
                    key: "email",
                    render: (value: string) => {
                      const isHit = matchedUserEmails.some(
                        (e) => normalizeEmail(e) === normalizeEmail(value),
                      );
                      return isHit ? (
                        <Text strong>
                          {value} <Tag color="green">match</Tag>
                        </Text>
                      ) : (
                        value
                      );
                    },
                  },
                  {
                    title: "User ID",
                    dataIndex: "id",
                    key: "id",
                    ellipsis: true,
                  },
                ]}
                locale={{ emptyText: "No users linked to this company" }}
              />
            </>
          )}

          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              disabled={selectedCompanyId == null}
              onClick={onContinue}
            >
              Continue to booking insights
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};
